import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';

export type ZoomFrame = {
  path: string;
  label: string;
  // A selector for the element that had focus when we zoomed in from this page,
  // so zooming back out can re-highlight exactly where the user was (a list row
  // cell, a marked field, etc.). Undefined when we couldn't locate one.
  focus?: string;
};

// Capture a stable selector for the element focused right now, so we can restore
// it after a round-trip. Prefers a list grid cell's keynav index (the common
// case — zooming in from a list row), then an id, then a `js-` marker class.
function captureZoomFocus(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const el = document.activeElement as HTMLElement | null;
  if (!el || el === document.body) return undefined;
  const cell = el.closest('[data-keynav-index]');
  if (cell) return `[role="grid"] [data-keynav-index="${cell.getAttribute('data-keynav-index')}"]`;
  if (el.id) return `#${CSS.escape(el.id)}`;
  const js = Array.from(el.classList).find((c) => c.startsWith('js-'));
  if (js) return `.${CSS.escape(js)}`;
  return undefined;
}

type ZoomState = {
  stack: ZoomFrame[];
  currentLabel: string;
  hasPageActions: boolean;
  // A selector to focus once the destination of a zoom-OUT has rendered (set by
  // popTo, consumed + cleared by the AppLayout restore effect).
  pendingFocus: string | null;
  // The page (path) pendingFocus belongs to, set by popTo: the restore waits
  // until the router is there. Null when set otherwise (no wait).
  pendingFocusFor: string | null;
  // Set true by push() and popTo() right before the caller navigates. The
  // AppLayout watcher checks this on every pathname change to decide
  // whether the navigation is an intentional zoom drill (keep stack) or
  // some unrelated navigation like a main-nav click (clear stack). Reset
  // by AppLayout itself once consumed.
  intentionalNav: boolean;
  // Like intentionalNav, but STAMPED with the pathname it applies to. Used by
  // useZoomBackstop: a lazy detail page seeds its crumb in a commit AFTER the
  // pathname already changed (Suspense resolve), so the AppLayout watcher never
  // runs to consume a plain boolean and it would leak onto the next page. By
  // stamping the page's own path, AppLayout only honours the seed while we're
  // actually on that page — a leftover stamp can't preserve the stack elsewhere.
  navFor: string | null;
  push: (frame: ZoomFrame) => void;
  popTo: (index: number) => ZoomFrame | null;
  setCurrentLabel: (label: string) => void;
  setHasPageActions: (b: boolean) => void;
  setIntentionalNav: (b: boolean) => void;
  setPendingFocus: (sel: string | null) => void;
  clear: () => void;
};

// Every legitimate push()/popTo() is followed SYNCHRONOUSLY by a navigation,
// whose pathname change lets AppLayout's watcher consume `intentionalNav`.
// When the navigation targets the pathname we're already on (e.g. the copilot
// linking to the current page), no watcher run ever consumes the flag — and a
// dangling intent made the NEXT unrelated navigation keep a stale stack (the
// "Receipts ›" crumb surviving onto Admin Center). Self-expire it: an intent
// still unconsumed seconds later cannot belong to a real pending drill.
// (Worst case, on an ultra-slow lazy chunk the drill's stack gets cleared and
// the destination's useZoomBackstop re-seeds its own crumb — degraded, never
// stale.) Each arm() restarts the window so back-to-back drills keep theirs.
let intentExpiry: ReturnType<typeof setTimeout> | null = null;
function armIntentExpiry(set: (s: Partial<ZoomState>) => void) {
  if (intentExpiry) clearTimeout(intentExpiry);
  intentExpiry = setTimeout(() => {
    intentExpiry = null;
    if (useZoomStore.getState().intentionalNav) set({ intentionalNav: false });
  }, 2500);
}

export const useZoomStore = create<ZoomState>((set, get) => ({
  stack: [],
  currentLabel: '',
  hasPageActions: false,
  pendingFocus: null,
  pendingFocusFor: null,
  intentionalNav: false,
  navFor: null,
  // On zoom-IN, remember the focused element so a later zoom-OUT can restore it.
  push: (frame) => {
    set({ stack: [...get().stack, { ...frame, focus: frame.focus ?? captureZoomFocus() }], intentionalNav: true });
    armIntentExpiry(set);
  },
  popTo: (index) => {
    const s = get().stack;
    if (index < 0 || index >= s.length) return null;
    const target = s[index];
    // Restore the focus that was active when we left the page we're returning to.
    set({ stack: s.slice(0, index), intentionalNav: true, pendingFocus: target.focus ?? null, pendingFocusFor: target.focus ? target.path : null });
    armIntentExpiry(set);
    return target;
  },
  setCurrentLabel: (label) => set({ currentLabel: label }),
  setHasPageActions: (b) => set({ hasPageActions: b }),
  setIntentionalNav: (b) => set({ intentionalNav: b }),
  setPendingFocus: (sel) => set({ pendingFocus: sel, pendingFocusFor: null }),
  clear: () => set({ stack: [], currentLabel: '', hasPageActions: false, intentionalNav: false, navFor: null, pendingFocus: null, pendingFocusFor: null }),
}));

// Convenience: destination pages call `useZoomCrumb('Customer: ACME-001')`
// in render to publish their label to the breadcrumb in the sub-nav.
export function useZoomCrumb(label: string) {
  const setCurrentLabel = useZoomStore((s) => s.setCurrentLabel);
  useEffect(() => {
    setCurrentLabel(label);
    return () => setCurrentLabel('');
  }, [label, setCurrentLabel]);
}

// Backstop for detail pages: if the page is reached with NO zoom history
// (empty stack) — a direct URL, a refresh, OR after returning from a drill
// that left the stack empty — seed a single "back to <list>" crumb so the
// breadcrumb path is always present (e.g. "Invoices › INV-001" instead of the
// bare section tabs). We also flip `intentionalNav` so AppLayout's navigation
// watcher KEEPS this seed instead of clearing it (this child effect runs
// before that parent effect). No-op when a real drill chain already exists,
// so deeper paths are never clobbered.
// Current ROUTER pathname, stamped by AppLayout's navigation watcher before it
// processes each location change. Under the MemoryRouter window.location is
// permanently '/', so anything needing "which page are we really on" must read
// this instead.
export const routerPathRef: { current: string | null } = { current: null };

export function useZoomBackstop(listPath: string, listLabel: string) {
  // The page's own ROUTER path (window.location is useless under MemoryRouter —
  // it stamped '/' for every page, which let a stale re-seed from a page still
  // mounted under Suspense survive onto the NEXT section: the "Invoices ›"
  // crumb showing up on Production).
  const herePath = useLocation().pathname;
  useEffect(() => {
    // Stamp the keep-signal with THIS page's own pathname (not a global
    // boolean). A lazy detail page seeds in a post-navigation commit that the
    // AppLayout watcher never re-runs for, so a boolean would leak onto the
    // NEXT page (an invoice's "Invoices ›" crumb showing up on Admin Center).
    // With a path stamp, AppLayout only honours the seed while we're on this
    // page and drops it the moment we navigate anywhere else.
    const here = herePath;
    const seed = () => {
      const st = useZoomStore.getState();
      if (st.stack.length === 0) {
        useZoomStore.setState({ stack: [{ path: listPath, label: listLabel }], navFor: here });
      } else if (st.stack.length === 1 && st.stack[0].path === listPath) {
        // The only crumb is already this list — e.g. it was seeded for the
        // matching /<entity>/new page and then we landed on the freshly-created
        // record via a replace-nav. Keep the crumb for this page (otherwise a
        // brand-new document shows no "back to <list>" path at all).
        useZoomStore.setState({ navFor: here });
      } else if (st.stack.length === 1 && st.navFor === here) {
        // We own the only frame (our own earlier seed) but the list path has
        // since resolved differently — e.g. the SO detail page seeds "Orders"
        // before the doc loads, then learns it's a QUOTE. Correct the frame.
        useZoomStore.setState({ stack: [{ path: listPath, label: listLabel }], navFor: here });
      }
    };
    seed();
    // Backstop-of-the-backstop: if ANYTHING empties the stack while this page
    // is still mounted (a navigation-watcher race around replace-navs from
    // "+ New", a Suspense-ordering quirk…), re-seed on the next tick so a
    // detail page NEVER sits without its "back to <list>" crumb. The timeout
    // + alive/pathname guards make this a no-op the moment we truly leave
    // (popTo empties the stack right before navigating away — by the time the
    // timeout fires, the page is unmounted or the pathname has changed).
    let alive = true;
    const unsub = useZoomStore.subscribe((s) => {
      if (s.stack.length === 0) {
        setTimeout(() => {
          if (!alive) return;
          // Router moved on (AppLayout stamps routerPathRef before clearing) —
          // this page's crumb must NOT re-seed onto the next page.
          if (routerPathRef.current !== null && routerPathRef.current !== here) return;
          seed();
        }, 0);
      }
    });
    return () => { alive = false; unsub(); };
  }, [listPath, listLabel, herePath]);
}
